import LayoutTransition from 'lib/ui/Animations/LayoutTransition'
import Box from 'lib/ui/Box'
import Grid from 'lib/ui/Grid'
import Heading from 'lib/ui/Heading'
import Link from 'next/link'
import { DefaultSeo } from 'next-seo'
import { animateIn } from 'utils/animations'
import theme from 'utils/theme'

import {
  IDashboardLayout,
  IDefaults,
  ISideStyles
} from './DashboardLayout.types'
import MobileNav from './MobileNav'
import Navigation from './Navigation'
import * as css from './styles'
import UserMenu from './UserMenu'

export const defaultProps: IDefaults = {
  breakpoint: 'lg',
  sideWidth: 25,
  mobileHeaderHeight: 8.2
}

const DashboardLayout = ({
  sideBackground = 'primary',
  defaults = {
    ...defaultProps
  },
  titleProps: {
    size = 'md',
    color = 'grey25',
    margin = [0, 0, 2],
    ...otherTitleProps
  } = {},
  brandColor,
  children,
  hideMenu,
  customUserMenu,
  logo,
  masthead,
  navigation,
  sideContent,
  sideContentBottom,
  title,
  styles,
  user
}: IDashboardLayout) => {
  const { breakpoint, sideWidth, mobileHeaderHeight } = defaults
  return (
    <>
      <DefaultSeo title={title} />
      <Grid
        breakpoint={breakpoint}
        gap={0}
        columns={`${theme.size(sideWidth)} auto`}
      >
        <Box padding={0} margin={0} outerColor={sideBackground}>
          <div
            data-testid='desktop-header'
            css={css.sidebar({ sideWidth, breakpoint }, { styles })}
          >
            {navigation && navigation.length > 0 && (
              <header>
                <Box
                  padding={[2, 0]}
                  styles={css.sidebarContent(
                    styles?.sidebarContent as ISideStyles
                  )}
                >
                  <div
                    css={css.logo(
                      { mobileHeaderHeight, breakpoint },
                      { styles }
                    )}
                  >
                    <Link href='/dashboard' passHref>
                      {logo}
                    </Link>
                  </div>
                  <Navigation
                    brandColor={brandColor}
                    items={navigation}
                    styles={styles?.desktopNavigation}
                  />
                  {sideContent}
                  {!hideMenu && (
                    <>
                      {customUserMenu || (
                        <UserMenu brandColor={brandColor} user={user} />
                      )}
                    </>
                  )}
                  {sideContentBottom}
                </Box>
              </header>
            )}
          </div>
          <div
            css={css.navStylesMobile({
              mobileHeaderHeight,
              breakpoint,
              sideWidth
            })}
          >
            <Box outerColor='surface100' padding={0}>
              <header>
                <div
                  css={css.logo({ mobileHeaderHeight, breakpoint }, { styles })}
                >
                  {logo}
                </div>
                <div css={css.menu}>
                  <MobileNav
                    brandColor={brandColor}
                    offset={mobileHeaderHeight}
                  >
                    <Navigation
                      brandColor={brandColor}
                      isMobile={true}
                      items={navigation}
                    />
                    {sideContent}
                    {!hideMenu && (
                      <UserMenu brandColor={brandColor} user={user} />
                    )}
                  </MobileNav>
                </div>
              </header>
            </Box>
          </div>
        </Box>

        <Box
          outerColor='surface100'
          padding={0}
          fullHeight={true}
          styles={{
            outer: {
              position: 'relative',
              overflow: 'auto'
            }
          }}
        >
          <main css={css.wrapper({ mobileHeaderHeight, breakpoint }, styles)}>
            <LayoutTransition
              {...animateIn({ transition: 'slide', x: -50, y: 0 })}
            >
              <div css={styles?.title}>
                {masthead && <>{masthead}</>}
                {title && !masthead && (
                  <Heading
                    tag='h1'
                    size={size}
                    color={color}
                    margin={margin}
                    {...otherTitleProps}
                  >
                    {title}
                  </Heading>
                )}
              </div>
              <div css={styles?.content}>{children}</div>
            </LayoutTransition>
          </main>
        </Box>
      </Grid>
    </>
  )
}

export default DashboardLayout
